import {AlignType, HStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish, PartialWithNull, suffixTestId, TestIdProps} from 'shared/web';

import {getJustifyFromAlign} from '../../utils/getJustifyFromAlign';
import {ChipsProps} from '../Chip/Chips';
import {ButtonAction, ButtonActionProps} from './components/ButtonAction';
import {
  ButtonWithTooltipAction,
  ButtonWithTooltipActionProps,
} from './components/ButtonWithTooltipAction';
import {ChipsAction, ChipsActionProps} from './components/ChipsAction';
import {DropdownAction, DropdownActionProps} from './components/DropdownAction';
import {FormButtonAction, FormButtonActionProps} from './components/FormButtonAction';
import {IconButtonAction, IconButtonActionProps} from './components/IconButtonAction';
import {
  IconButtonWithTooltipAction,
  IconButtonWithTooltipActionProps,
} from './components/IconButtonWithTooltipAction';

interface CustomActionProps extends PartialWithNull<Omit<ChipsProps, 'type'>> {
  type: 'custom';
  component: React.ReactNode;
}

export type Action =
  | ButtonActionProps
  | DropdownActionProps
  | IconButtonActionProps
  | IconButtonWithTooltipActionProps
  | FormButtonActionProps
  | ButtonWithTooltipActionProps
  | ChipsActionProps
  | CustomActionProps;

export interface ActionsProps extends TestIdProps {
  actions: Action[] | Nullish;
  size?: 'small' | 'default';
  align?: AlignType;
}

export function Actions(props: ActionsProps) {
  if (isNilOrEmpty(props.actions)) {
    return null;
  }

  return (
    <HStack
      spacing={2}
      align="center"
      justify={getJustifyFromAlign(props.align)}
      data-testid={props['data-testid']}
    >
      {props.actions?.map((action, key) => {
        const testId = action['data-testid'] ?? `[${key}]`;
        if (action.type === 'button') {
          return (
            <ButtonAction
              key={`buttonAction-[${action.title}]-[${key}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...action}
            />
          );
        }

        if (action.type === 'buttonWithTooltip') {
          return (
            <ButtonWithTooltipAction
              key={`buttonWithTooltipAction-[${action.title}]-[${key}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...action}
            />
          );
        }

        if (action.type === 'form-button') {
          return (
            <FormButtonAction
              key={`formButtonAction-[${action.title}]-[${key}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...action}
            />
          );
        }

        if (action.type === 'iconButton') {
          return (
            <IconButtonAction
              key={`iconButtonAction-[${key}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...action}
            />
          );
        }

        if (action.type === 'iconButtonWithTooltip') {
          return (
            <IconButtonWithTooltipAction
              key={`iconButtonWithTooltipAction-[${key}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...action}
            />
          );
        }

        if (action.type === 'chips') {
          return (
            <ChipsAction
              key={`chipsAction-[${key}]`}
              size={props.size}
              data-testid={suffixTestId(testId, props)}
              {...action}
            />
          );
        }

        if (action.type === 'custom') {
          return action.component;
        }

        return (
          <DropdownAction
            key={`dropdownAction-[${key}]`}
            size={props.size}
            data-testid={suffixTestId(testId, props)}
            {...action}
          />
        );
      })}
    </HStack>
  );
}
